export default (params) => ({
    filesBag: [],
    requests: {},
    successMessage: 'Download has been completed!',
    failedDownloadMessage: 'Could not retrieve file!',
    totalLeft: 0,
    processing: false,
    // Bumped on dismiss. An in-flight process() run resumes after its awaits with the shared state
    // already reset, so it compares the generation it started in before touching anything.
    generation: 0,
    init() {
        window.addEventListener('attachment::download', ({detail}) => {
            this.download(detail.attachment)
        });

        this.$watch('totalLeft', (value) => {
            if (value === 0) {
                this.$wire.set('downloading', false)
            }
        })
    },
    dismiss() {
        this.generation++
        clearTimeout(this.clearTimer)
        Object.values(this.requests).forEach(req => {
            try {
                req.abort()
            } catch (e) {}
        })
        this.requests = {}
        this.filesBag = []
        this.totalLeft = 0
        this.$wire.set('downloading', false)
    },
    checkCleanup(generation) {
        if (generation === this.generation && this.totalLeft === 0 && this.filesBag.length > 0) {
            clearTimeout(this.clearTimer)

            this.clearTimer = setTimeout(() => {
                if (generation === this.generation && this.totalLeft === 0) {
                    this.filesBag = this.filesBag.filter((file) => file.error)
                }
            }, 3000)
        }
    },
    finalize(file, index, status, generation) {
        if (generation !== this.generation || this.filesBag[index] !== file) {
            return
        }

        if (file.finalized) {
            return
        }
        file.finalized = true
        file.downloading = false
        delete this.requests[index]

        if (status === 'success') {
            file.completed = true
        } else if (status === 'error') {
            file.error = true
            this.notify('error', this.failedDownloadMessage)
        } else if (status === 'abort') {
            file.progress = 100
            file.abort = true
        }

        this.totalLeft--

        this.checkCleanup(generation)
    },
    hasPending() {
        return this.filesBag.some((file) => !file.completed && !file.error && !file.downloading)
    },
    prepare(params) {
        let hasInitialized = this.filesBag.filter((file) => {
            return file.key === params.key
        }).length

        if (!hasInitialized) {
            this.totalLeft++

            params.completed = false
            params.error = false
            params.downloading = false
            this.filesBag.push(params)
        }
    },
    async createTemporaryUrl(key, fullName) {
        return await this.$wire.call('createPresignedUrl', key, fullName).then(url => {
            return url
        })
    },

    async process() {
        if (this.processing) {
            return
        }

        this.processing = true

        const generation = this.generation

        try {
            for (let index = 0; index < this.filesBag.length; index++) {
                if (generation !== this.generation) {
                    return
                }

                const file = this.filesBag[index]

                if (file.completed || file.error || file.downloading) {
                    continue
                }

                const key = file.key
                const extension = file.extension
                const fileName = file.fileName
                const extDot = extension ? `.${extension.toLowerCase()}` : ''
                const fullName = (extDot && !fileName.toLowerCase().endsWith(extDot))
                    ? `${fileName}.${extension}`
                    : fileName

                this.filesBag[index].fullName = fullName
                this.filesBag[index].downloading = true

                let percentCompleted
                let downloaded

                try {
                    const url = await this.createTemporaryUrl(key, fullName).then(url => {
                        return url
                    });

                    if (generation !== this.generation || this.filesBag[index] !== file) {
                        return
                    }

                    const req = new XMLHttpRequest();

                    this.requests[index] = req

                    req.open("GET", url, true);
                    req.timeout = 300000;

                    const startTime = new Date().getTime();

                    req.addEventListener("progress", (event) => {
                        if (generation !== this.generation || this.filesBag[index] !== file) {
                            return
                        }
                        if (event.lengthComputable) {
                            const percent = (event.loaded / event.total) * 100;
                            percentCompleted = Math.floor(percent);

                            const duration = (new Date().getTime() - startTime) / 1000;
                            const bps = event.loaded / duration
                            downloaded = defaults(Math.floor(bps))

                            const time = (event.total - event.loaded) / bps;
                            const seconds = Math.floor(time % 60);
                            const minutes = Math.floor(time / 60);

                            this.filesBag[index].progress = percentCompleted
                            this.filesBag[index].downloaded = downloaded

                            if (minutes > 0) {
                                this.filesBag[index].remaining = minutes + " min " + seconds + " sec remaining"
                            } else {
                                this.filesBag[index].remaining = seconds + " sec remaining"
                            }
                        }
                    }, false);

                    req.addEventListener('abort', () => {
                        this.finalize(file, index, 'abort', generation)
                    });

                    req.responseType = 'blob';

                    req.addEventListener('load', () => {
                        if (generation !== this.generation || this.filesBag[index] !== file) {
                            return
                        }
                        if (req.status >= 200 && req.status < 300) {
                            const objectUrl = window.URL.createObjectURL(req.response);
                            const link = document.createElement('a');
                            link.href = objectUrl;
                            link.download = fullName;
                            link.click();
                            setTimeout(() => {
                                window.URL.revokeObjectURL(objectUrl);
                            }, 100);

                            this.finalize(file, index, 'success', generation)
                        } else {
                            this.finalize(file, index, 'error', generation)
                        }
                    });

                    req.addEventListener('error', () => {
                        this.finalize(file, index, 'error', generation)
                    });

                    req.addEventListener('timeout', () => {
                        this.finalize(file, index, 'error', generation)
                    });

                    await new Promise((resolve) => {
                        req.addEventListener('loadend', resolve, {once: true});
                        req.send();
                    });
                } catch (e) {
                    if (generation !== this.generation || this.filesBag[index] !== file) {
                        return
                    }

                    this.finalize(file, index, 'error', generation)
                    console.log(`Could not retrieve file from S3: ${e.message}`)
                }
            }

            this.checkCleanup(generation)
        } finally {
            this.processing = false

            // An aborted run leaves anything queued after the dismiss unprocessed, because the
            // download() that queued it found processing still true.
            if (generation !== this.generation && this.hasPending()) {
                this.process()
            }
        }
    },
    async download(params) {
        try {
            this.prepare(params)

            await this.process()
        } catch (e) {
            this.notify('error', this.failedDownloadMessage)
            console.log(`Could not retrieve file from S3: ${e.message}`)
        }
    },
    notify(type, message) {
        window.dispatchEvent(new CustomEvent('notify', {
            detail: {type: type, title: message}
        }));
    }
})
